import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { MdClose } from "react-icons/md";
import { PiFileZipBold } from "react-icons/pi";
import { ContentContext } from "../contexts/ContentContext";
import { requestAiExport, requestAiExportCount } from "../requests/export_requests";

const MAX_DAYS = 186; // about six months, same limit as the backend

const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dayLabel = (iso) => iso.split("-").reverse().join(".");

// Must match the backend's file name.
const zipName = ({ dateFrom, dateTo, dateField }) => `destek_ai_${dateField === "updated" ? "guncellenen_" : ""}${dateFrom}_${dateTo}.zip`;

// Default range: the whole previous month.
const previousMonthRange = () => {
    const now = new Date();
    return [isoDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)), isoDay(new Date(now.getFullYear(), now.getMonth(), 0))];
};

const daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000) + 1;

// Ask where to save before the (slow) request: the browser only allows the
// save dialog right after a click. Browsers without the API download normally.
const pickSaveTarget = async (suggestedName) => {
    if (!window.showSaveFilePicker) return null;
    return window.showSaveFilePicker({
        suggestedName,
        types: [{ description: "ZIP", accept: { "application/zip": [".zip"] } }],
    });
};

const saveBlob = async (blob, handle, name) => {
    if (handle) {
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

// The export keeps running here after the dialog closes; its progress shows next to the button.
export const AiExportButton = () => {
    const { t } = useTranslation("common");
    const { openErrorModal } = useContext(ContentContext);
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(null); // {busy: true} | {name, mb, asked}

    const startExport = async (options) => {
        const name = zipName(options);
        let handle = null;
        try {
            handle = await pickSaveTarget(name);
        } catch (e) {
            if (e.name === "AbortError") return; // save dialog closed: keep the export dialog open
        }
        setOpen(false);
        setStatus({ busy: true });
        try {
            const blob = await requestAiExport(options);
            await saveBlob(blob, handle, name);
            setStatus({ name: handle ? handle.name : name, mb: (blob.size / 1e6).toFixed(1), asked: !!handle });
            setTimeout(() => setStatus(null), 8000);
        } catch (error) {
            console.error(error.status, error.message);
            setStatus(null);
            // 404: nothing in the range (the dialog normally prevents this)
            openErrorModal(error.status === 404 ? { ...error, message: t("ai_export.empty") } : error);
        }
    };

    return (
        <div className="flex items-center gap-3">
            {status?.busy && (
                <span className="flex items-center gap-2 text-sm">
                    <span className="loading loading-spinner loading-sm" /> {t("ai_export.preparing")}
                </span>
            )}
            {status && !status.busy && (
                <span className="text-sm text-green-700 dark:text-green-400">
                    {t(status.asked ? "ai_export.done_saved" : "ai_export.done_downloaded", { name: status.name, mb: status.mb })}
                </span>
            )}
            <button className="btn btn-sm bg-primary hover:bg-sky-700 text-white border-none" disabled={status?.busy} onClick={() => setOpen(true)}>
                <PiFileZipBold size={16} /> {t("ai_export.button")}
            </button>
            {open && <AiExportModal onClose={() => setOpen(false)} onStart={startExport} />}
        </div>
    );
};

const AiExportModal = ({ onClose, onStart }) => {
    const { t } = useTranslation("common");
    const [dateFrom, setDateFrom] = useState(() => previousMonthRange()[0]);
    const [dateTo, setDateTo] = useState(() => previousMonthRange()[1]);
    const [dateField, setDateField] = useState("created");
    const [profile, setProfile] = useState("large");
    const [formats, setFormats] = useState(["md", "jsonl"]);
    const [includePii, setIncludePii] = useState(false);
    const [count, setCount] = useState(null); // null: not known yet, -1: counting

    const today = isoDay(new Date());
    const nDays = dateFrom && dateTo ? daysBetween(dateFrom, dateTo) : 0;
    const rangeError = !dateFrom || !dateTo
        ? t("ai_export.error_months")
        : nDays < 1
            ? t("ai_export.error_order")
            : dateTo > today
                ? t("ai_export.error_future")
                : nDays > MAX_DAYS
                    ? t("ai_export.error_range", { max: MAX_DAYS })
                    : null;

    // Count the tickets for the chosen days, so an empty range is visible before exporting.
    useEffect(() => {
        setCount(null);
        if (rangeError) return;
        let cancelled = false;
        const timer = setTimeout(() => {
            setCount(-1);
            requestAiExportCount({ dateFrom, dateTo, dateField })
                .then((data) => !cancelled && setCount(data.tickets))
                .catch((error) => {
                    console.error(error.status, error.message);
                    if (!cancelled) setCount(null); // export still possible; the backend checks again
                });
        }, 400);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [dateFrom, dateTo, dateField, rangeError]);

    const toggleFormat = (f) => setFormats((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

    const handleExport = () => onStart({ dateFrom, dateTo, dateField, profile, formats, includePii });

    const option = (selected, onSelect, title, help) => (
        <label className={`flex items-start gap-2 border rounded-md p-2 cursor-pointer ${selected ? "border-primary bg-sky-50 dark:bg-sky-900" : "border-gray-300 dark:border-gray-600"}`}>
            <input type="radio" className="radio radio-sm mt-0.5" checked={selected} onChange={onSelect} />
            <span className="flex flex-col">
                <span className="text-sm font-medium">{title}</span>
                <span className="text-xs opacity-70">{help}</span>
            </span>
        </label>
    );
    const radio = (value, title, help) => option(profile === value, () => setProfile(value), title, help);

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
            <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg shadow-xl w-[94vw] max-w-xl p-5 flex flex-col gap-4 max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg flex items-center gap-2"><PiFileZipBold size={20} /> {t("ai_export.title")}</h3>
                    <button className="btn btn-sm btn-ghost" onClick={onClose}><MdClose /></button>
                </div>
                <p className="text-sm opacity-80">{t("ai_export.intro")}</p>

                <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium">{t("ai_export.range")}</span>
                    {/* Only "not after today": tying each box to the other made later days unselectable in the start box. */}
                    <div className="flex flex-wrap items-center gap-2">
                        <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={dateFrom} max={today} onChange={(e) => setDateFrom(e.target.value)} />
                        <span>–</span>
                        <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={dateTo} max={today} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <span className="text-xs opacity-70">{t("ai_export.range_help", { max: MAX_DAYS })}</span>
                    {rangeError ? (
                        <span className="text-xs text-red-600">{rangeError}</span>
                    ) : (
                        <span className={`text-sm ${count === 0 ? "text-red-600" : ""}`}>
                            <b>{t("ai_export.selected", { from: dayLabel(dateFrom), to: dayLabel(dateTo), days: nDays })}</b>
                            {" · "}
                            {count === -1 || count === null ? t("ai_export.counting") : count === 0 ? t("ai_export.empty") : t("ai_export.count", { count })}
                        </span>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium">{t("ai_export.date_field")}</span>
                    {option(dateField === "created", () => setDateField("created"), t("ai_export.date_field_created"), t("ai_export.date_field_created_help"))}
                    {option(dateField === "updated", () => setDateField("updated"), t("ai_export.date_field_updated"), t("ai_export.date_field_updated_help"))}
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium">{t("ai_export.target")}</span>
                    {radio("large", t("ai_export.target_large"), t("ai_export.target_large_help"))}
                    {radio("small", t("ai_export.target_small"), t("ai_export.target_small_help"))}
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium">{t("ai_export.formats")}</span>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="checkbox checkbox-sm" checked={formats.includes("md")} onChange={() => toggleFormat("md")} />
                        {t("ai_export.format_md")}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="checkbox checkbox-sm" checked={formats.includes("jsonl")} onChange={() => toggleFormat("jsonl")} />
                        {t("ai_export.format_jsonl")}
                    </label>
                </div>

                <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={includePii} onChange={(e) => setIncludePii(e.target.checked)} />
                    <span className="flex flex-col">
                        {t("ai_export.pii")}
                        <span className="text-xs text-orange-600">{t("ai_export.pii_help")}</span>
                    </span>
                </label>

                <p className="text-xs opacity-70">{t("ai_export.save_help")}</p>

                <div className="flex justify-end gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={onClose}>{t("warning.close")}</button>
                    <button className="btn btn-sm bg-primary hover:bg-sky-700 text-white border-none" disabled={!!rangeError || formats.length === 0 || count === 0} onClick={handleExport}>
                        {t("ai_export.export")}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
